/**
 * Soft-nav can leave foreign <link rel="stylesheet"> tags in <head>.
 *
 * Invariant: on destination mount, disable FOREIGN group sheets and re-enable
 * the active group's sheets. Prefer `disabled` over removeChild so React/Next
 * layout-managed <link precedence> nodes stay in the DOM (removing them
 * desyncs the virtual DOM and breaks soft-nav until hard refresh).
 */

export type RouteStylesheetGroup =
  | 'marketing'
  | 'login'
  | 'ai-bio'
  | 'account-extra'
  | 'company';

/** Href substrings that identify stylesheets owned by each route group. */
export const ROUTE_STYLESHEET_SNIPPETS: Record<RouteStylesheetGroup, readonly string[]> = {
  marketing: [
    'maxcdn.bootstrapcdn.com/bootstrap',
    'bootstrap-icons',
    // Leading slash avoids matching `/company-assets/.../landing-assets/css/style.css`
    '/landing-assets/css/style.css',
    'common-assets/navbar-assets/css/style.css',
    'common-assets/extension-assets/extension.css',
    'common-assets/css/common.css',
    'resume-analyzer-assets/css/resume-analyzer.css',
    'salary-insights-report-assets/css/salary-insights-report.css',
    'coverletter-assets/css/coverletter.css',
    'linkedin-assets/css/style.css',
    // Legacy content pages (hoisted React links; scrub on account soft-nav)
    'resume-examples-assets/',
    'compare-assets/',
    '/resume-examples/assets/',
    'guides-assets/',
    '/assets/css/example-main',
    '/assets/css/guides',
    // Marketing Google Fonts (must re-enable after foreign scrub).
    // Do NOT scrub Material Icons / Material Symbols — shared with account (like FA).
    'family=Inter+Tight',
    'family=Piazzolla',
    'family=Poppins',
  ],
  login: [
    'materialize.min.css',
    // Legacy public paths (may linger from older builds / test injects)
    'login-assets/css/style.css',
    'login-assets/css/bootstrap.min.css',
  ],
  'ai-bio': [
    'family=Inter:',
    'family=Playfair+Display',
  ],
  'account-extra': [
    // Account fonts/icons are shared; do not scrub FA / Material Symbols globally.
  ],
  // Only page CSS — do NOT scrub shared Google Font CDNs used by marketing.
  company: ['company-assets/'],
};

/** Body classes that marketing / login attach and must not linger. */
export const ROUTE_BODY_CLASSES = {
  marketing: ['kw-marketing-body', 'sticky-navbar'] as const,
  login: ['kw-login-body', 'kw-login-ready'] as const,
  company: ['kw-company-body'] as const,
} as const;

export function hrefMatchesSnippets(href: string, snippets: readonly string[]): boolean {
  if (!href) {
    return false;
  }
  return snippets.some((snippet) => href.includes(snippet));
}

export function collectStylesheetHrefs(doc: Document = document): string[] {
  const hrefs: string[] = [];
  doc.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    const href = node.getAttribute('href');
    if (href) {
      hrefs.push(href);
    }
  });
  return hrefs;
}

/**
 * Disable <link rel="stylesheet"> whose href matches any snippet for the given groups.
 * Call only for FOREIGN groups on destination mount — never for the owning layout's group on cleanup.
 * Disabling (instead of removeChild) preserves React/Next layout link ownership.
 */
export function removeRouteStylesheets(
  groups: RouteStylesheetGroup | RouteStylesheetGroup[],
  doc: Document = document,
): number {
  const list = Array.isArray(groups) ? groups : [groups];
  const snippets = list.flatMap((group) => ROUTE_STYLESHEET_SNIPPETS[group] || []);
  if (snippets.length === 0) {
    return 0;
  }

  let disabledCount = 0;
  doc.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    const link = node as HTMLLinkElement;
    const href = link.getAttribute('href') || '';

    if (hrefMatchesSnippets(href, snippets)) {
      if (!link.disabled) {
        link.disabled = true;
        disabledCount += 1;
      }
    }
  });

  return disabledCount;
}

/**
 * Re-enable stylesheets for the active route group after a prior foreign scrub
 * disabled them (soft-nav back to the owning layout).
 */
export function enableRouteStylesheets(
  groups: RouteStylesheetGroup | RouteStylesheetGroup[],
  doc: Document = document,
): number {
  const list = Array.isArray(groups) ? groups : [groups];
  const snippets = list.flatMap((group) => ROUTE_STYLESHEET_SNIPPETS[group] || []);
  if (snippets.length === 0) {
    return 0;
  }

  let enabledCount = 0;
  doc.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    const link = node as HTMLLinkElement;
    const href = link.getAttribute('href') || '';

    if (hrefMatchesSnippets(href, snippets)) {
      if (link.disabled) {
        link.disabled = false;
        enabledCount += 1;
      }
    }
  });

  return enabledCount;
}

/** Disable stylesheets that do not belong on the account shell (login + marketing + ai-bio). */
export function removeForeignAccountStylesheets(doc: Document = document): number {
  return removeRouteStylesheets(['login', 'marketing', 'ai-bio'], doc);
}

export function removeBodyClasses(classNames: readonly string[], body: HTMLElement = document.body): void {
  classNames.forEach((className) => {
    body.classList.remove(className);
  });
}

export function clearMarketingBodyLeftovers(body: HTMLElement = document.body): void {
  removeBodyClasses(ROUTE_BODY_CLASSES.marketing, body);
}

export function clearCompanyBodyLeftovers(body: HTMLElement = document.body): void {
  removeBodyClasses(ROUTE_BODY_CLASSES.company, body);
  delete body.dataset.companyVariant;
}

export function clearLoginBodyLeftovers(body: HTMLElement = document.body): void {
  removeBodyClasses(ROUTE_BODY_CLASSES.login, body);
}
